import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Stepper } from '@/components/ui/stepper';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import DocumentUploadDropzone from '@components/documents/DocumentUploadDropzone';
import DocumentMetadataForm from '@components/documents/DocumentMetadataForm';
import UploadProgressTracker from '@components/documents/UploadProgressTracker';
import UploadFileList from '@components/documents/UploadFileList';
import DuplicateFileDialog from '@components/documents/DuplicateFileDialog';
import { useCapabilities } from '@hooks/useCapabilities';
import UploadLimitsPanel from '@components/documents/UploadLimitsPanel';
import { useDocumentUpload, type UploadMetadata } from '@hooks/useDocumentUpload';
import { useConfirm } from '@hooks/useConfirm';

const DocumentUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const confirm = useConfirm();
  const [activeStep, setActiveStep] = useState(0);
  const [metadataDirty, setMetadataDirty] = useState(false);

  const describeBackgroundProcessing = useCallback(
    () => t('upload.backgroundProcessingMessage'),
    [t],
  );
  const upload = useDocumentUpload({ describeBackgroundProcessing });

  const steps = useMemo(
    () => [
      { id: 'select', label: t('upload.steps.selectFiles') },
      { id: 'metadata', label: t('upload.steps.addMetadata') },
      { id: 'process', label: t('upload.steps.uploadProcess') },
    ],
    [t],
  );

  const viewUploaded = () =>
    navigate(upload.completedDocumentId ? `/documents/${upload.completedDocumentId}` : '/documents');

  const submitMetadata = async (metadata: UploadMetadata) => {
    setActiveStep(2);
    await upload.startUpload(metadata);
  };

  const leavePage = async () => {
    if (
      upload.isUploading &&
      !(await confirm({
        title: t('upload.confirmLeave.title'),
        description: t('upload.confirmLeave.body'),
        confirmLabel: t('upload.confirmLeave.confirm'),
      }))
    ) {
      return;
    }
    navigate('/documents');
  };

  // Step 1 holds the only unsaved input in the wizard; the form unmounts on the
  // way back to step 0 and its state goes with it.
  const goBack = async () => {
    if (
      activeStep === 1 &&
      metadataDirty &&
      !(await confirm({
        title: t('upload.confirmBack.title'),
        description: t('upload.confirmBack.body'),
        confirmLabel: t('upload.confirmBack.confirm'),
      }))
    ) {
      return;
    }
    setMetadataDirty(false);
    setActiveStep((step) => Math.max(step - 1, 0));
  };

  const finished = upload.files.every((f) => ['completed', 'error'].includes(f.status));
  const hasErrors = upload.files.some((f) => f.status === 'error');
  const inBackground = upload.files.some((f) => f.status === 'processing' && Boolean(f.documentId));

  return (
    <div className="@container space-y-6">
      <header className="flex items-center gap-2">
        <TooltipIconButton
          label={t('documents.returnToDocuments')}
          icon={<ArrowLeft className="size-4" strokeWidth={2} />}
          onClick={leavePage}
        />
        <h1 className="text-2xl font-semibold tracking-tight text-balance">
          {t('upload.pageTitle')}
        </h1>
      </header>

      <Card>
        <CardContent className="py-5">
          <Stepper
            steps={steps}
            activeStep={activeStep}
            allComplete={finished && !hasErrors && upload.files.length > 0}
            progressLabel={(current, total) => t('upload.stepProgress', { current, total })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 py-5">
          {activeStep === 0 && (
            <>
              <div>
                <h2 className="text-base font-medium">{t('upload.selectDocuments')}</h2>
                <p className="mt-1 text-sm text-muted-foreground text-pretty">
                  {t('upload.selectDocumentsDesc')}
                </p>
              </div>
              <UploadLimitsPanel
                status={capabilities.status}
                upload={capabilities.capabilities?.upload}
                onRetry={capabilities.retry}
              />
              {capabilities.status === 'ready' && capabilities.capabilities && (
                <DocumentUploadDropzone
                  onFilesSelected={upload.addFiles}
                  allowedMimeTypes={capabilities.capabilities.upload.allowedMimeTypes}
                  maxFileSize={capabilities.capabilities.upload.maxFileSizeBytes}
                  maxFiles={capabilities.capabilities.upload.maxSimultaneousFiles}
                />
              )}
              <UploadFileList files={upload.files} onRemove={upload.removeFile} />
            </>
          )}

          {activeStep === 1 && (
            <>
              <div>
                <h2 className="text-base font-medium">{t('upload.addMetadata')}</h2>
                <p className="mt-1 text-sm text-muted-foreground text-pretty">
                  {t('upload.addMetadataDesc')}
                </p>
              </div>
              <DocumentMetadataForm
                onSubmit={submitMetadata}
                fileCount={upload.files.length}
                onDirtyChange={setMetadataDirty}
              />
            </>
          )}

          {activeStep === 2 && (
            <>
              <div>
                <h2 className="text-base font-medium">{t('upload.uploadProgressTitle')}</h2>
                <p className="mt-1 text-sm text-muted-foreground text-pretty">
                  {t('upload.uploadProgressDesc')}
                </p>
              </div>

              <UploadProgressTracker uploadFiles={upload.files} onRetry={upload.retryFile} />

              {inBackground && (
                <Alert>
                  <AlertTitle>{t('upload.backgroundProcessingTitle')}</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>{t('upload.backgroundProcessingDesc')}</p>
                    <Button variant="outline" size="sm" onClick={viewUploaded}>
                      {t('upload.viewDocuments')}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {finished && upload.files.length > 0 && (
                <Alert variant={hasErrors ? 'destructive' : 'default'}>
                  <AlertTitle>
                    {hasErrors ? t('upload.uploadFinishedWithErrors') : t('upload.uploadComplete')}
                  </AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p className="revy-figure">
                      {t('upload.uploadCompleteDesc', {
                        completed: upload.files.filter((f) => f.status === 'completed').length,
                        total: upload.files.length,
                      })}
                    </p>
                    <Button variant="outline" size="sm" onClick={viewUploaded}>
                      {t('upload.viewDocuments')}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={goBack}
          disabled={activeStep === 0}
        >
          {t('upload.back')}
        </Button>

        {activeStep === 0 && (
          <Button
            onClick={() => setActiveStep((step) => Math.min(step + 1, steps.length - 1))}
            disabled={!upload.canAdvance || upload.isUploading}
          >
            {t('upload.next')}
          </Button>
        )}
      </div>

      <DuplicateFileDialog file={upload.duplicate} onResolve={upload.resolveDuplicate} />
    </div>
  );
};

export default DocumentUploadPage;
